import { connect, JSONCodec, StringCodec, type NatsConnection } from 'nats.ws'
import { globalConfig } from './global-config'
import { Cookie } from '@repo/tools'

let connectionPromise: Promise<NatsConnection> | null = null

export const jc = JSONCodec()
export const sc = StringCodec()

export const getNatsConnection = async (): Promise<NatsConnection> => {
  if (connectionPromise) {
    return connectionPromise.catch((error) => {
      // Reset connection promise on error to allow retry
      connectionPromise = null
      console.error('NATS connection error:', error)
      throw error
    })
  }

  const accessToken = Cookie.get('access_token')

  // Don't attempt connection if no token is available
  if (!accessToken) {
    const error = new Error('No access token available for NATS connection')
    connectionPromise = Promise.reject(error)
    return connectionPromise.catch((err) => {
      connectionPromise = null
      throw err
    })
  }

  connectionPromise = connect({
    servers: globalConfig.websocketUrl || 'wss://ws-dev.malekcloud.com',
    token: accessToken,
    timeout: 10000, // 10 seconds timeout
    reconnect: true,
    maxReconnectAttempts: 5,
    reconnectTimeWait: 2000,
  }).catch((error) => {
    // Reset connection promise on error to allow retry
    connectionPromise = null

    // Handle authorization errors gracefully
    if (
      error.message?.includes('Authorization Violation') ||
      error.message?.includes('authorization')
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'NATS connection failed: Authorization Violation (token may be invalid or expired)',
        )
      }
    } else {
      console.error('NATS connection failed:', error)
    }
    throw error
  })

  return connectionPromise
}

// Initialize connection with error handling to prevent unhandled rejection
export const nc = getNatsConnection().catch((error) => {
  // Silently handle initial connection error
  // Connection will be retried when actually needed
  if (process.env.NODE_ENV === 'development') {
    const errorMessage = error.message || String(error)
    if (
      errorMessage.includes('No access token') ||
      errorMessage.includes('Authorization Violation')
    ) {
      // Don't log these as warnings - they're expected when user is not authenticated
      return null as unknown as NatsConnection
    }
    console.warn(
      'NATS initial connection failed (this is expected if server is not available):',
      errorMessage,
    )
  }
  return null as unknown as NatsConnection
})
